import { NATS_INBOX_VERBS, NATS_KV_BUCKET_VERBS, NATS_REQUEST_ENDPOINT_VERBS, NATS_STREAM_VERBS } from "./stream-verbs.js";
import { RESOURCE_SLUG_PATTERN } from "./slug-grammar.js";
import { PERMISSION_KEY_PATTERN } from "./permission-key.js";
import { z } from "zod";
//#region src/resource-grants.ts
/**
* The data-plane resource-grant contract: the type catalog, the permission grammar, the wire
* shapes of the grant routes and the refusal codes (dp-resource-grants, R-002).
*
* A resource is a `(type, id)` pair. A grant is one active row of `auth.resource_grants` that gives
* a subject (a principal, a group or a role) one verb on one resource. The holders of a type's
* `manage` verb are the resource's manage-grant holders; no column records them anywhere else.
*
* Producers and consumers:
* - `@zion/core-api` writes and reads the rows: the resolver in `src/authz` (WS-2), the grant routes
*   and MCP tools in `src/admin/resource-grants` (WS-3), the NATS credential compile (WS-4), the
*   channel audience (WS-5), the role-to-app grants (WS-6), the machine-principal routes (WS-7) and
*   the app-declared type policy (WS-10).
* - `@zion/sdk-nestjs`'s `@RequireResourcePermission` decorator and guard classify permissions, ask
*   the type's existence probe first and hand the decision to the plane's resolver (WS-8, R-026).
* - `@zion/sdk-node`'s grants client parses every wire body with the schemas here (WS-9).
*
* The identifier rule (2026-09-30-agent-streams-and-app-owned-groups, index contracts). A `principal`
* subject id, a group member's `principalId` and the resource id of every `release_channel`,
* `machine_principal` and `group` row is the platform row id: the JWT `sub`, a `users.id` or a
* `machine_principals.id`, or a `groups.id` or channel id, stored as the row's own lower-case id
* (dp-resource-grants R-045). The four NATS grant types name their resources in their own grammars
* (2026-10-05-stream-resource-permissions, overview D-10 to D-23):
* - a `nats_stream` id is the principal's slug (`machine_principals.agent_id`) or a platform stream
*   name from `PLATFORM_STREAMS`; enroll refuses a slug that could equal one;
* - a `nats_inbox` id is the inbox owner's slug;
* - a `nats_request_endpoint` id is `team/<groups.slug>` (`NATS_REQUEST_ENDPOINT_ID_PATTERN`);
* - a `nats_kv_bucket` id is a bucket name from `NATS_KV_BUCKETS`.
* No other identifier family crosses the wire.
*
* Every export here is pure: no clock, no I/O, no module state beyond frozen constants.
*/
/** A platform type name: lowercase, digits and underscores, no colon and no dot. */
const PLATFORM_TYPE_PATTERN = /^[a-z][a-z0-9_]*$/;
/**
* A verb of a platform or app-declared type, and a type name inside `app.<slug>.<type>`. The same
* literal is the `pattern` of `$defs/resourceType` in `app-manifest.schema.json`; the manifest test
* compares the two by behaviour over one sample table.
*/
const NAME_TOKEN_PATTERN = /^[a-z][a-z0-9-]*$/;
/** The upper bound on a name token: the manifest's `maxLength`, and a Kubernetes label value's. */
const NAME_TOKEN_MAX_LENGTH = 63;
/** The upper bound on an app slug inside an app-declared type (`app-admin-inputs.ts`, #969). */
const APP_SLUG_MAX_LENGTH = 63;
/** The upper bound on a whole permission string, checked before any pattern runs. */
const PERMISSION_MAX_LENGTH = 256;
/** The prefix every app-declared type carries. */
const APP_DECLARED_TYPE_PREFIX = "app";
/** The type whose verbs are an app's declared permission keys (R-008). */
const APP_TYPE = "app";
/** A UUID in the 8-4-4-4-12 hex shape, any version nibble, either case. */
const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Printable ASCII with no space: the character class of every id and type on the wire. */
const VISIBLE_ASCII = /^[!-~]+$/;
/**
* The verb that makes its holder a manage-grant holder of a resource. The resolver lets it satisfy
* every other verb of its type; a route asks for the narrowest verb it needs (I-4). The credential
* compile is the exception: it reads exact verbs, so a `manage` row of a NATS grant type adds no
* subject to a NATS credential (R-006).
*
* Callers: every package that builds a `manage` permission. No package writes the literal `manage`
* itself (defaults row "The verb strings … come from `RESOURCE_TYPE_CATALOG`").
*/
const RESOURCE_GRANT_MANAGE_VERB = "manage";
/**
* The three kinds of grant subject, in the order `auth.resource_grants.subject_kind` lists them.
* `principal` is a `users` or `machine_principals` id, `group` is a `groups` id and `role` is an org
* role slug. The `owner` role is never a row: the standing rule covers it (Q11).
*/
const RESOURCE_GRANT_SUBJECT_KINDS = [
	"principal",
	"group",
	"role"
];
/** A frozen record with no prototype, so an untrusted key never reads an inherited member. */
function nullPrototypeRecord(entries) {
	return Object.freeze(Object.assign(Object.create(null), entries));
}
/** A frozen catalog entry whose arrays are frozen too. */
function catalogEntry(entry) {
	const frozen = {
		verbs: Object.freeze([...entry.verbs]),
		...entry.openVerbs === true ? { openVerbs: true } : {},
		subjects: Object.freeze([...entry.subjects]),
		...entry.subjectsByVerb === void 0 ? {} : { subjectsByVerb: nullPrototypeRecord(entry.subjectsByVerb) }
	};
	return Object.freeze(frozen);
}
/**
* The platform resource types and what each admits (R-002, R-008, R-013; the `group` row is
* 2026-09-30-agent-streams-and-app-owned-groups, decision 9). The one source of every
* verb string, type string and subject rule on the data plane; no package writes a verb literal.
*
* | type | verbs | subjects |
* |---|---|---|
* | `nats_stream` | `manage`, `append`, `read` ({@link NATS_STREAM_VERBS}); `append` is author only, and the route refuses it as `verb_reserved` | principal, group |
* | `nats_inbox` | `manage`, `post`, `receive` ({@link NATS_INBOX_VERBS}) | principal, group |
* | `nats_request_endpoint` | `manage`, `call`, `serve` ({@link NATS_REQUEST_ENDPOINT_VERBS}) | principal, group |
* | `nats_kv_bucket` | `manage`, `read`, `write` ({@link NATS_KV_BUCKET_VERBS}) | principal, group |
* | `release_channel` | `manage`, `assign`, `use` | principal, group, role; `use` refuses role |
* | `machine_principal` | `manage` | principal, group |
* | `group` | `manage` | principal, group, role |
* | `app` | open: the app's declared keys, no `manage` | role |
*
* The `app` row lists `role` alone, and the generic grant route and resource check read this list.
* So they count a role's `app` row and no `principal` or `group` row of type `app`. The apps-catalog
* routes also write `app` rows with a `group` subject or a `principal` subject that is a person
* (2026-10-29-app-permission-subjects, R-001, R-005). Those rows reach a person only through the
* app token claim build, which counts them for an active person and never for a machine principal.
*
* `group` is a resource type and a subject kind, and the two are different roles of one word. The
* resource id of a `group` row is a `groups.id`; a `group` subject is a `groups.id` too. So "subject
* group G holds `group:manage` on group H" is a legal row: every member of G manages H. A holder of
* `group:manage` adds and removes members, nests child groups, renames and deletes the group; the
* `groups:manage` org key keeps doing all of it (2026-09-30-agent-streams-and-app-owned-groups,
* decisions 8 and 9). `group` has no narrower verb: reading a group stays on the `groups:read` org key.
*
* An app-declared type (`app.<slug>.<type>`, {@link isAppDeclaredType}) is not a row here. Its verbs
* come from the newest `release.app_manifests` row, which must list `manage` (R-017), and it admits
* principal, group and role subjects.
*
* The record has a null prototype and is frozen at every level, so `RESOURCE_TYPE_CATALOG[x]` for an
* untrusted `x` is either an entry or `undefined`, never an inherited member. The declared type says
* every key maps to an entry; a caller holding an untrusted key checks `Object.hasOwn` first.
*/
const RESOURCE_TYPE_CATALOG = nullPrototypeRecord({
	nats_stream: catalogEntry({
		verbs: NATS_STREAM_VERBS,
		subjects: ["principal", "group"]
	}),
	nats_inbox: catalogEntry({
		verbs: NATS_INBOX_VERBS,
		subjects: ["principal", "group"]
	}),
	nats_request_endpoint: catalogEntry({
		verbs: NATS_REQUEST_ENDPOINT_VERBS,
		subjects: ["principal", "group"]
	}),
	nats_kv_bucket: catalogEntry({
		verbs: NATS_KV_BUCKET_VERBS,
		subjects: ["principal", "group"]
	}),
	release_channel: catalogEntry({
		verbs: [
			RESOURCE_GRANT_MANAGE_VERB,
			"assign",
			"use"
		],
		subjects: [
			"principal",
			"group",
			"role"
		],
		subjectsByVerb: { use: ["principal", "group"] }
	}),
	machine_principal: catalogEntry({
		verbs: [RESOURCE_GRANT_MANAGE_VERB],
		subjects: ["principal", "group"]
	}),
	group: catalogEntry({
		verbs: [RESOURCE_GRANT_MANAGE_VERB],
		subjects: [
			"principal",
			"group",
			"role"
		]
	}),
	[APP_TYPE]: catalogEntry({
		verbs: [],
		openVerbs: true,
		subjects: ["role"]
	})
});
/**
* The rules no grant row expresses. Today there is one: the org `owner` role holds `manage` on
* every type (Q11). `admin` has no rule and is granted like any subject.
*
* The plane's resolver applies it third, after the site-wide wildcard and the delegated `scp`
* narrowing, and only when `scp` is absent (R-004); the SDK guard applies no standing rule of its
* own (R-026). The credential compile never applies it
* (R-012). A grant naming a role listed here is refused with `standing_rule_not_grantable`, and the
* rule never counts toward the last-manage refusal. Frozen at both levels.
*/
const STANDING_RULES = Object.freeze([Object.freeze({
	role: "owner",
	resourceType: "*",
	verb: RESOURCE_GRANT_MANAGE_VERB
})]);
/**
* True when `type` names an app-declared resource type: `app.<slug>.<type>`, three dot-separated
* parts. The slug matches `RESOURCE_SLUG_PATTERN` (two to 63 characters). The type name matches
* `^[a-z][a-z0-9-]*$` (one to 63 characters). The name carries no colon, because a permission
* splits on its first colon (F-7).
*
* Callers: the resolver and the decorator's classifier, which route an app type away from the
* catalog; WS-10's policy, which reads the type's verbs from the app's manifest. It says nothing
* about whether the app exists or declares the type. Any other string, including `app` itself and
* a platform type name, answers `false`. Never throws.
*/
function isAppDeclaredType(type) {
	if (typeof type !== "string") return false;
	const parts = type.split(".");
	if (parts.length !== 3) return false;
	const [prefix, slug, name] = parts;
	return prefix === APP_DECLARED_TYPE_PREFIX && isAppSlug(slug) && isNameToken(name);
}
/**
* Builds the app-declared type name `app.<slug>.<type>` from an app slug and a type name the app's
* manifest declares. The result always passes {@link isAppDeclaredType}.
*
* Callers: WS-10's policy, which rebuilds a split type to confirm it, and any app or test that names
* its own type. Throws `TypeError` naming the part when `slug` fails `RESOURCE_SLUG_PATTERN` or runs
* past 63 characters, or when `type` fails `^[a-z][a-z0-9-]*$` or runs past 63 characters. It never
* mints a name the parser would refuse.
*/
function appDeclaredType(slug, type) {
	if (!isAppSlug(slug)) throw new TypeError(`appDeclaredType: ${JSON.stringify(slug)} is not an app slug`);
	if (!isNameToken(type)) throw new TypeError(`appDeclaredType: ${JSON.stringify(type)} is not a resource type name`);
	return `${APP_DECLARED_TYPE_PREFIX}.${slug}.${type}`;
}
/**
* Splits a permission string `<type>:<verb>` on its first colon and checks both parts' grammar.
* Returns `null` on any reject; never throws.
*
* Accepted input classes:
* - A platform-shaped type (`^[a-z][a-z0-9_]*$`) other than `app`, with a verb `^[a-z][a-z0-9-]*$`:
*   `nats_stream:read`.
* - The type `app`, with a verb that is an app permission key under `PERMISSION_KEY_PATTERN`:
*   `app:docs:read` answers type `app`, verb `docs:read` (R-008).
* - An app-declared type ({@link isAppDeclaredType}), with a verb `^[a-z][a-z0-9-]*$`:
*   `app.notes.board:edit`.
*
* Rejected: a non-string; a string over 256 characters; no colon; an empty type or verb; a type in
* neither grammar; a colon inside the verb of any type but `app`; any whitespace or upper case.
*
* It checks grammar only. A well-formed type the catalog does not list (`cp:read`) parses; the
* caller decides whether the type is known and whether its verbs include this one. Callers: the
* resolver, the grant routes, the decorator's classifier, the SDK client, and
* {@link resourceGrantPermission}.
*/
function parseResourceGrantPermission(s) {
	if (typeof s !== "string" || s.length > PERMISSION_MAX_LENGTH) return null;
	const colon = s.indexOf(":");
	if (colon <= 0) return null;
	const resourceType = s.slice(0, colon);
	const verb = s.slice(colon + 1);
	if (resourceType === APP_TYPE) return PERMISSION_KEY_PATTERN.test(verb) ? {
		resourceType,
		verb
	} : null;
	if (!(PLATFORM_TYPE_PATTERN.test(resourceType) || isAppDeclaredType(resourceType)) || !isNameToken(verb)) return null;
	return {
		resourceType,
		verb
	};
}
/**
* Builds the permission string `<type>:<verb>` for a grant row or a check.
*
* Callers: every package that asks for or writes a permission, instead of joining strings itself.
* Throws `TypeError` when the result would not parse back to the same `(type, verb)` through
* {@link parseResourceGrantPermission}, so a malformed permission never reaches a row, a cache key
* or a guard. It does not check the verb against the catalog.
*/
function resourceGrantPermission(type, verb) {
	const permission = `${type}:${verb}`;
	const parsed = parseResourceGrantPermission(permission);
	if (parsed === null || parsed.resourceType !== type || parsed.verb !== verb) throw new TypeError(`resourceGrantPermission: ${JSON.stringify(type)} and ${JSON.stringify(verb)} do not form a permission`);
	return permission;
}
function isAppSlug(slug) {
	return slug.length <= APP_SLUG_MAX_LENGTH && RESOURCE_SLUG_PATTERN.test(slug);
}
function isNameToken(name) {
	return typeof name === "string" && name.length <= NAME_TOKEN_MAX_LENGTH && NAME_TOKEN_PATTERN.test(name);
}
const subjectKindSchema = z.enum(RESOURCE_GRANT_SUBJECT_KINDS);
const uuidSchema = z.string().regex(UUID_SHAPE);
const subjectIdSchema = z.string().min(1).max(128).regex(VISIBLE_ASCII);
const permissionSchema = z.string().min(1).max(PERMISSION_MAX_LENGTH).regex(VISIBLE_ASCII);
const resourceTypeSchema = z.string().min(1).max(160).regex(VISIBLE_ASCII);
const resourceIdSchema = z.string().min(1).max(256).regex(VISIBLE_ASCII);
/**
* One grant as `GET /v1/admin/resource-grants` lists it and `POST` returns it: every column of
* `auth.resource_grants`, timestamps as ISO-8601 UTC strings. `.strict()`, so an extra key refuses.
*
* - `id`, `organizationId`: UUID-shaped.
* - `subjectKind`: one of {@link RESOURCE_GRANT_SUBJECT_KINDS}; `subjectId` is 1 to 128 printable
*   characters (a UUID for principal and group, a slug for role).
* - `permission` (1 to 256), `resourceType` (1 to 160), `resourceId` (1 to 256): printable ASCII.
* - `grantedBy`: the writer's UUID from the verified JWT `sub`, or `null` for an operator-path write
*   (F-13).
* - `revokedAt`: `null` on an active row. The routes list active rows only; the SDK fake keeps
*   revoked rows and shows them through the same shape.
*
* The inferred type is {@link ResourceGrantDto}, so the Drizzle row type in `@zion/core-api` keeps
* the name `ResourceGrantRow` (R-002). Producer: WS-3's service. Consumers: WS-9's client and fake.
*/
const resourceGrantRowSchema = z.object({
	id: uuidSchema,
	organizationId: uuidSchema,
	subjectKind: subjectKindSchema,
	subjectId: subjectIdSchema,
	permission: permissionSchema,
	resourceType: resourceTypeSchema,
	resourceId: resourceIdSchema,
	grantedAt: z.iso.datetime(),
	grantedBy: uuidSchema.nullable(),
	revokedAt: z.iso.datetime().nullable()
}).strict();
/**
* The body of `POST /v1/admin/resource-grants` and the `grant_resource_permission` MCP tool.
* `.strict()`: an `orgId`, `grantedBy` or any other key refuses, because the org and the writer come
* from the verified token only.
*
* It checks structure and bounds only (the same bounds as {@link resourceGrantRowSchema}). The
* grammar, the type match, the catalog verb, the subject rule and the standing-rule subject are the
* service's checks, each with its own code in {@link RESOURCE_GRANT_REFUSALS} (WS-3 section 5), so
* this schema never answers for them.
*/
const createResourceGrantInputSchema = z.object({
	subjectKind: subjectKindSchema,
	subjectId: subjectIdSchema,
	permission: permissionSchema,
	resourceType: resourceTypeSchema,
	resourceId: resourceIdSchema
}).strict();
/**
* The query of `GET /v1/admin/resource-grants` and the `list_resource_grants` MCP tool: one
* resource, both keys required. `.strict()`, so an extra key refuses. Structure and bounds only; an
* unknown type is the service's `unknown_resource_type`.
*/
const listResourceGrantsQuerySchema = z.object({
	resourceType: resourceTypeSchema,
	resourceId: resourceIdSchema
}).strict();
/**
* The two forms of a list by subject (2026-10-16-digest-pin-and-grants-by-subject, G-3):
* - `direct`: the active rows stored on the subject itself. The default.
* - `effective`: for a `principal` subject only, the active rows of the principal, of its roles and
*   of its groups with the parent walk. Each row keeps its own `subjectKind` and `subjectId`, so the
*   caller sees where a grant comes from. A `group` or `role` subject with `effective` refuses with
*   `expand_not_applicable`. Neither form shows the owner standing rule or the site wildcard, which
*   are not rows.
*/
const RESOURCE_GRANTS_EXPAND = ["direct", "effective"];
/** The `expand` query value; see {@link RESOURCE_GRANTS_EXPAND}. */
const resourceGrantsExpandSchema = z.enum(RESOURCE_GRANTS_EXPAND);
/**
* The most rows a list by subject answers (G-4). A subject with more rows that match refuses with
* `too_many_grants` (422), so the caller narrows the list by `resourceType` or `permission`.
*/
const RESOURCE_GRANTS_SUBJECT_LIST_MAX = 1e3;
/**
* The subject form of `GET /v1/admin/resource-grants` (G-1): the active grants of one subject in the
* caller's org. `.strict()`, so an extra key refuses.
*
* - `subjectKind`, `subjectId`: the subject, as {@link createResourceGrantInputSchema} names it.
* - `resourceType`, `permission`: optional filters; `permission` matches the stored value exactly.
* - `expand`: {@link resourceGrantsExpandSchema}; absent means `direct`.
*
* Producer of the answer: core-api, a bare array of {@link resourceGrantRowSchema} ordered by
* `grantedAt` then `id`. Caller: the SDK client's `list`.
*/
const listResourceGrantsBySubjectQuerySchema = z.object({
	subjectKind: subjectKindSchema,
	subjectId: subjectIdSchema,
	resourceType: resourceTypeSchema.optional(),
	permission: permissionSchema.optional(),
	expand: resourceGrantsExpandSchema.optional()
}).strict();
/**
* Every query `GET /v1/admin/resource-grants` takes: the resource form
* ({@link listResourceGrantsQuerySchema}) or the subject form
* ({@link listResourceGrantsBySubjectQuerySchema}). Both arms are `.strict()`, so a query that
* mixes their keys matches neither. The `list_resource_grants` MCP tool keeps the resource form.
*/
const listResourceGrantsAnyQuerySchema = z.union([listResourceGrantsQuerySchema, listResourceGrantsBySubjectQuerySchema]);
/**
* The query of `GET /v1/resource-grants/by-subject` (G-2), the app arm: an app service bearer lists
* the active grants of one subject on one of its own declared types. `.strict()`.
*
* `resourceType` is required here. The server refuses a type outside `app.<own slug>.` with 403.
* The other keys are those of {@link listResourceGrantsBySubjectQuerySchema}.
*/
const listAppResourceGrantsBySubjectQuerySchema = listResourceGrantsBySubjectQuerySchema.extend({ resourceType: resourceTypeSchema });
/**
* The body of `GET /v1/resource-grants/effective?permission=<type:verb>` (R-003): which resources
* the forwarded end user holds one verb on. `.strict()`.
*
* - `organizationId`: the verified forwarded token's `orgId`, UUID-shaped.
* - `subjectId`: the verified forwarded token's `sub`, 1 to 128 printable characters.
* - `permission`: the permission asked, echoed; it must parse through
*   {@link parseResourceGrantPermission}.
* - `allResources`: `true` when the standing rule admits the end user (the org `owner`, Q11).
* - `resourceIds`: explicit and `manage`-implied grants, distinct and ascending by UTF-16 code unit.
*   A duplicate or an out-of-order id refuses the body, so a client never caches a list the server
*   did not normalise.
*
* Producer: WS-3's effective route. Consumer: WS-9's HTTP resolver, which compares the echo.
*/
const effectiveResourceGrantsSchema = z.object({
	organizationId: uuidSchema,
	subjectId: subjectIdSchema,
	permission: permissionSchema.refine((p) => parseResourceGrantPermission(p) !== null, { message: "permission must be <type>:<verb>" }),
	allResources: z.boolean(),
	resourceIds: z.array(resourceIdSchema).refine((ids) => ids.every((id, i) => i === 0 || ids[i - 1] < id), { message: "resourceIds must be distinct and ascending" })
}).strict();
/**
* The grant collection route: `GET` lists one resource's active grants as a bare array, `POST`
* creates one grant and answers 201 with it. Owner: WS-3's controller. Caller: WS-9's client.
*/
const RESOURCE_GRANTS_PATH = "/v1/admin/resource-grants";
/**
* The path of one grant, for `DELETE`, which answers 204 (R-015). Returns
* `RESOURCE_GRANTS_PATH + '/' + encodeURIComponent(id)`.
*
* Throws `TypeError` when `id` is not UUID-shaped, so a caller byte such as `..`, `/` or `?` can
* never reach the path. Callers: WS-9's client after its own UUID check, and route tests.
*/
function resourceGrantPath(id) {
	if (typeof id !== "string" || !UUID_SHAPE.test(id)) throw new TypeError(`resourceGrantPath: ${JSON.stringify(id)} is not a grant id`);
	return `${RESOURCE_GRANTS_PATH}/${encodeURIComponent(id)}`;
}
/**
* The effective-grants route (R-003): answers {@link effectiveResourceGrantsSchema} for the
* forwarded end user. Owner: WS-3. Caller: WS-9's HTTP resolver.
*/
const EFFECTIVE_RESOURCE_GRANTS_PATH = "/v1/resource-grants/effective";
/**
* The app arm of the list by subject (G-2): answers a bare array of {@link resourceGrantRowSchema}
* for {@link listAppResourceGrantsBySubjectQuerySchema}. Owner: core-api. Caller: the SDK client's
* `listBySubject`.
*/
const RESOURCE_GRANTS_BY_SUBJECT_PATH = "/v1/resource-grants/by-subject";
/**
* The `code` field of every refusal the grant routes, the MCP tools and the effective route answer,
* keyed by a camel-case name. Producers throw `{ code }` bodies with these values; the SDK client
* and tests match on them. Frozen.
*
* | code | status | when |
* |---|---|---|
* | `permission_type_mismatch` | 400 | the permission fails the grammar, or names a type other than `resourceType` |
* | `unknown_resource_type` | 400 | the type is neither a catalog type nor an app-declared type |
* | `resource_type_not_grantable` | 400 | the type is `app`: app grants have their own apps-catalog routes (R-008) |
* | `standing_rule_not_grantable` | 400 | a `role` subject that {@link STANDING_RULES} names |
* | `subject_kind_not_grantable` | 400 | the subject kind is outside the type's subjects for the verb (R-013) |
* | `verb_not_declared` | 400 | the verb is outside the type's verbs; the body carries `availableVerbs` |
* | `manifest_not_synced` | 400 | no `release.app_manifests` row exists for the app (WS-10) |
* | `resource_types_unreadable` | 400 | the app's `resourceTypes` fails the parse (WS-10) |
* | `resource_type_not_declared` | 400 | the app's manifest does not declare the type (WS-10) |
* | `unknown_subject` | 400 | the subject does not exist in the caller's org |
* | `verb_reserved` | 400 | a NATS grant verb that `isNatsGrantVerbReserved` reserves: `nats_stream:append` on every id, any verb on a platform stream or KV bucket outside its `orgGrantableVerbs`; for every writer, the org `owner` included; checked before the writer policy (2026-10-05-stream-resource-permissions, overview D-11, D-14, D-23; R-022) |
* | `verb_not_applicable` | 400 | kept by ruling R-001. Since the four-type split (overview D-10) every verb of a NATS type's catalog row applies to every resource of the type, so no check of this contract answers it |
* | `resource_not_found` | 404 | the resource does not exist in the caller's org (404 before 403) |
* | `grant_not_found` | 404 | no active grant with that id in the caller's org |
* | `duplicate_grant` | 409 | an active grant with the same tuple exists |
* | `group_already_assigned` | 409 | a group `release_channel:use` grant, when the group holds an active `use` grant on another channel: one group routes to one channel (R-013, R-037). Also a group move (`requireGroupChannelMove`, `replaceGroupAudience`) when the caller holds no `assign` on a channel the group would leave; a clear can answer it with no grant written (R-038) |
* | `last_manage` | 409 | the revoke would leave the resource with no active `manage` row |
* | `expand_not_applicable` | 400 | a list by subject with `expand: effective` names a `group` or `role` subject (G-3) |
* | `too_many_grants` | 422 | a list by subject matches more than {@link RESOURCE_GRANTS_SUBJECT_LIST_MAX} rows (G-4) |
*/
const RESOURCE_GRANT_REFUSALS = Object.freeze({
	permissionTypeMismatch: "permission_type_mismatch",
	unknownResourceType: "unknown_resource_type",
	resourceTypeNotGrantable: "resource_type_not_grantable",
	standingRuleNotGrantable: "standing_rule_not_grantable",
	subjectKindNotGrantable: "subject_kind_not_grantable",
	verbNotDeclared: "verb_not_declared",
	manifestNotSynced: "manifest_not_synced",
	resourceTypesUnreadable: "resource_types_unreadable",
	resourceTypeNotDeclared: "resource_type_not_declared",
	unknownSubject: "unknown_subject",
	verbReserved: "verb_reserved",
	verbNotApplicable: "verb_not_applicable",
	resourceNotFound: "resource_not_found",
	grantNotFound: "grant_not_found",
	duplicateGrant: "duplicate_grant",
	groupAlreadyAssigned: "group_already_assigned",
	lastManage: "last_manage",
	expandNotApplicable: "expand_not_applicable",
	tooManyGrants: "too_many_grants"
});
//#endregion
export { EFFECTIVE_RESOURCE_GRANTS_PATH, RESOURCE_GRANTS_BY_SUBJECT_PATH, RESOURCE_GRANTS_EXPAND, RESOURCE_GRANTS_PATH, RESOURCE_GRANTS_SUBJECT_LIST_MAX, RESOURCE_GRANT_MANAGE_VERB, RESOURCE_GRANT_REFUSALS, RESOURCE_GRANT_SUBJECT_KINDS, RESOURCE_TYPE_CATALOG, STANDING_RULES, appDeclaredType, createResourceGrantInputSchema, effectiveResourceGrantsSchema, isAppDeclaredType, listAppResourceGrantsBySubjectQuerySchema, listResourceGrantsAnyQuerySchema, listResourceGrantsBySubjectQuerySchema, listResourceGrantsQuerySchema, parseResourceGrantPermission, resourceGrantPath, resourceGrantPermission, resourceGrantRowSchema, resourceGrantsExpandSchema };

//# sourceMappingURL=resource-grants.js.map